/**
 * Summary Service
 * 
 * Handles reading session summary generation, status polling, and downloads.
 * Integrates with backend summary generation API endpoints.
 */

import { getApiUrl } from '../utils/apiUtils';

// Type definitions
export interface SessionSummaryStatusResponse {
  story_session_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  message: string;
  started_at: string;
  completed_at?: string;
  error_message?: string;
  current_step?: string;
  progress_percent?: number;
}

export interface RubricScore {
  score: number;
  description: string;
  benchmark?: string;
  wcpm?: number;
  accuracy_percent?: number;
  total_words?: number;
  words_correct?: number;
  expression_quality?: string;
  phrasing_quality?: string;
}

export interface RubricAssessment {
  rate: RubricScore;
  accuracy: RubricScore;
  prosody: RubricScore;
  overall_score: number;
  grade_level: string;
  assessment_period: string;
}

export interface PronunciationHighlight {
  word: string;
  accuracy_score: number;
  status: 'mastered' | 'improving' | 'needs_practice';
  tip?: string;
}

export interface QuizScoreDetails {
  score_text: string;
  total_questions: number;
  correct_answers: number;
}

export interface RoleplayParticipationDetails {
  participation_text: string;
  characters_played: string[];
  total_interactions: number;
}

export interface PronunciationAnalysis {
  total_words_assessed: number;
  avg_accuracy_score: number;
  words_mastered: number;
  words_improving: number;
  words_challenging: number;
  challenging_words: PronunciationHighlight[];
  mastered_words: PronunciationHighlight[];
  error_patterns: string[];
}

export interface TimeMetrics {
  total_duration_seconds: number;
  reading_duration_formatted: string;
  avg_words_per_minute: number;
  started_at: string;
  completed_at: string;
}

export interface ReaderSummary {
  overall_performance: string;
  achievement_badge: 'excellent' | 'great' | 'good' | 'keep_trying';
  pronunciation_highlights: (string | PronunciationHighlight)[];
  quiz_score?: string | QuizScoreDetails;
  roleplay_participation?: string | RoleplayParticipationDetails;
  encouragement_message: string;
  improvement_tips: string[];
  reading_duration: string;
  words_read: number;
}

export interface TeacherSummary {
  student_performance_overview: string;
  rubric_assessment: RubricAssessment;
  pronunciation_analysis: PronunciationAnalysis;
  quiz_analysis?: any;
  roleplay_analysis?: any;
  time_metrics: TimeMetrics;
  strengths: string[];
  areas_for_improvement: string[];
  instructional_recommendations: string[];
  reading_level_assessment: string;
  next_steps: string[];
}

export interface SessionSummaryResponse {
  id: number;
  session_id: string;
  story_session_id: string;
  reader_summary: ReaderSummary;
  teacher_summary: TeacherSummary;
  generation_status: 'completed' | 'processing' | 'failed';
  generated_at: string;
  gpt_model: string;
  gpt_tokens_used: number;
}

class SummaryService {
  /**
   * Start generating a reading session summary
   */
  async generateSummary(storySessionId: string): Promise<SessionSummaryStatusResponse> {
    try {
      const response = await fetch(
        getApiUrl(`/summary/generate/${storySessionId}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to start summary generation');
      }
      
      return await response.json();
    } catch (error: any) {
      console.error('Error generating summary:', error);
      throw new Error(error.message || 'Failed to start summary generation');
    }
  }

  /**
   * Poll the status of summary generation
   */
  async pollSummaryStatus(storySessionId: string): Promise<SessionSummaryStatusResponse> {
    try {
      const response = await fetch(
        getApiUrl(`/summary/status/${storySessionId}`)
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to get summary status');
      }
      
      return await response.json();
    } catch (error: any) {
      console.error('Error polling summary status:', error);
      throw new Error(error.message || 'Failed to get summary status');
    }
  }

  /**
   * Get the complete generated summary
   */
  async getSummary(storySessionId: string): Promise<SessionSummaryResponse> {
    try {
      const response = await fetch(
        getApiUrl(`/summary/${storySessionId}`)
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to get summary');
      }
      
      return await response.json();
    } catch (error: any) {
      console.error('Error getting summary:', error);
      throw new Error(error.message || 'Failed to get summary');
    }
  }

  /**
   * Download summary in specified format (txt or pdf)
   */
  async downloadSummary(
    storySessionId: string,
    format: 'txt' | 'pdf',
    summaryType: 'reader' | 'teacher' = 'reader'
  ): Promise<void> {
    try {
      const response = await fetch(
        getApiUrl(`/summary/${storySessionId}/download/${format}?summary_type=${summaryType}`)
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to download summary');
      }

      // Create blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${summaryType}_summary_${storySessionId}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading summary:', error);
      throw new Error(error.message || 'Failed to download summary');
    }
  }

  /**
   * Poll summary generation status until completion
   * @param storySessionId Story session ID
   * @param onProgress Callback for progress updates
   * @param pollInterval Polling interval in milliseconds (default: 2000)
   * @param maxAttempts Maximum polling attempts (default: 60, i.e., 2 minutes)
   */
  async waitForCompletion(
    storySessionId: string,
    onProgress: (status: SessionSummaryStatusResponse) => void,
    pollInterval: number = 2000,
    maxAttempts: number = 60
  ): Promise<SessionSummaryStatusResponse> {
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const pollTimer = setInterval(async () => {
        try {
          attempts++;

          const status = await this.pollSummaryStatus(storySessionId);
          onProgress(status);

          if (status.status === 'completed') {
            clearInterval(pollTimer);
            resolve(status);
          } else if (status.status === 'failed') {
            clearInterval(pollTimer);
            reject(new Error(status.error_message || 'Summary generation failed'));
          } else if (attempts >= maxAttempts) {
            clearInterval(pollTimer);
            reject(new Error('Summary generation timeout'));
          }
        } catch (error) {
          clearInterval(pollTimer);
          reject(error);
        }
      }, pollInterval);
    });
  }

  /**
   * Check if a summary exists for a story session
   */
  async summaryExists(storySessionId: string): Promise<boolean> {
    try {
      await this.getSummary(storySessionId);
      return true;
    } catch (error: any) {
      if (error.message?.includes('not found') || error.message?.includes('404')) {
        return false;
      }
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Get achievement badge emoji based on badge type
   */
  getAchievementBadgeEmoji(badge: string): string {
    const badges: Record<string, string> = {
      excellent: '🏆',
      great: '⭐',
      good: '👍',
      keep_trying: '💪'
    };
    return badges[badge] || '🎉';
  }

  /**
   * Get rubric score color based on score
   */
  getRubricScoreColor(score: number, maxScore: number): string {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 90) return '#10B981'; // green
    if (percentage >= 70) return '#F59E0B'; // yellow
    if (percentage >= 50) return '#F97316'; // orange
    return '#EF4444'; // red
  }
}

// Export singleton instance
export const summaryService = new SummaryService();
export default summaryService;
